import { Link } from 'react-router';
import type { AuditEvent } from '@ai-secure-twin/shared';
import { api, ApiError } from '../../lib/api';
import { useAcrossEngagements } from '../../lib/useAcrossEngagements';
import { useAuth } from '../auth/auth';

const ACTION_LABEL: Record<string, string> = {
  APPLICATION_CREATED: 'Application registered',
  APPLICATION_UPDATED: 'Application updated',
  ENGAGEMENT_CREATED: 'Engagement created',
  ENGAGEMENT_UPDATED: 'Engagement updated',
  SCOPE_DEFINED: 'Scope version written',
  APPROVAL_RECORDED: 'Gate decision recorded',
  AGENT_RUN_REQUESTED: 'Engagement Lead asked for a proposal',
  SCAN_STARTED: 'Scan started',
  SCAN_FINISHED: 'Scan finished',
};

const formatInstant = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'medium' });

export function AuditPage() {
  const { user } = useAuth();
  const mayRead = user?.role === 'security-admin';

  const { items, isLoading, isError, error, isPartial } = useAcrossEngagements<AuditEvent>(
    'audit',
    api.listAudit,
  );

  const rows = [...items].sort((a, b) => b.item.at.localeCompare(a.item.at));

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <span className="eyebrow">Governance</span>
          <h1>Audit log</h1>
        </div>
      </div>

      <p className="muted" style={{ maxWidth: '68ch', marginTop: -8 }}>
        Every change writes an entry in the same transaction as the change itself, so a change
        cannot exist without its record. Entries are never edited or removed.
      </p>

      {!mayRead ? (
        <section className="card panel">
          <div className="empty">
            <p>Not available to your role.</p>
            <p className="muted">
              The trail names who did what, so only a security administrator may read it.
            </p>
          </div>
        </section>
      ) : (
        <>
          {isPartial ? (
            <p className="demo-notice" role="status">
              Some engagements could not be read, so this trail may be incomplete.
            </p>
          ) : null}

          <section className="card panel table-wrap">
            {isLoading ? (
              <p className="muted">Loading the trail…</p>
            ) : isError ? (
              <p className="login-error" role="alert">
                {error instanceof ApiError ? error.message : 'Could not load the audit trail.'}
              </p>
            ) : rows.length === 0 ? (
              <div className="empty">
                <p>Nothing recorded yet.</p>
                <p className="muted">Entries appear as soon as anything is created or changed.</p>
              </div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Action</th>
                    <th>Engagement</th>
                    <th>Changed</th>
                    <th>Actor</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ item, engagement }) => (
                    <tr key={item.id}>
                      <td className="mono muted" style={{ fontSize: 12 }}>
                        {formatInstant(item.at)}
                      </td>
                      <td>{ACTION_LABEL[item.action] ?? item.action}</td>
                      <td>
                        <Link to={`/app/engagements/${engagement.id}`} className="table-link">
                          {engagement.name}
                        </Link>
                      </td>
                      {/* Field names only — values could carry customer data. */}
                      <td className="muted" style={{ fontSize: 12 }}>
                        {item.changed?.join(', ') ?? '—'}
                      </td>
                      <td className="mono muted" style={{ fontSize: 11 }}>
                        {item.actorUserId}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}
